import React, { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import "./llama style/llama.css";

function loadUserState(uid) {
  try {
    const profileKey  = uid ? `studentProfile_${uid}` : 'studentProfile_guest';
    const shortlistKey = uid ? `savedColleges_${uid}` : 'savedColleges_guest';
    const criteriaKey  = uid ? `criteriaVisited_${uid}` : 'criteriaVisited_guest';

    const profile   = localStorage.getItem(profileKey);
    const shortlist = localStorage.getItem(shortlistKey);
    const criteria  = localStorage.getItem(criteriaKey);

    const savedCount = shortlist ? JSON.parse(shortlist).length : 0;
    const hasProfile = !!profile;
    const hasVisitedCriteria = !!criteria;

    return { hasProfile, savedCount, hasVisitedCriteria };
  } catch { return { hasProfile: false, savedCount: 0, hasVisitedCriteria: false }; }
}

const Llama = () => {
  const navigate = useNavigate();
  const auth = getAuth();
  const [firstName, setFirstName] = useState('');
  const [userState, setUserState] = useState({ hasProfile: false, savedCount: 0, hasVisitedCriteria: false });

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (!user) {
        navigate('/login');
      } else {
        const name = user.displayName || user.email?.split('@')[0] || '';
        setFirstName(name.split(' ')[0]);
        setUserState(loadUserState(user.uid));
      }
    });
    return () => unsubscribe();
  }, [auth, navigate]);

  const { hasProfile, savedCount, hasVisitedCriteria } = userState;

  const FEATURES = [
    {
      icon: '🎯',
      title: 'Profile Builder',
      desc: 'Chat with AI to build a complete college application profile — tailored to your country, grades, and goals.',
      cta: hasProfile ? 'Update Profile' : 'Build My Profile',
      href: '/profile-builder',
      accent: 'accent-blue',
      status: hasProfile
        ? { type: 'done', label: 'Profile complete' }
        : { type: 'todo', label: 'Not started — start here' },
    },
    {
      icon: '🏫',
      title: 'College Finder',
      desc: 'Search thousands of US universities, filter by SAT and acceptance rate, run gap analysis, and shortlist your schools.',
      cta: 'Find My College',
      href: '/rankings',
      accent: 'accent-purple',
      status: savedCount > 0
        ? { type: 'count', label: `${savedCount} college${savedCount !== 1 ? 's' : ''} saved` }
        : { type: 'todo', label: 'No colleges saved yet' },
    },
    {
      icon: '📋',
      title: 'Admission Criteria',
      desc: 'Understand exactly what US colleges want from international students — specific to your home country.',
      cta: 'Explore Criteria',
      href: '/criteria',
      accent: 'accent-teal',
      status: hasVisitedCriteria
        ? { type: 'done', label: 'Previously viewed' }
        : { type: 'todo', label: 'Not yet explored' },
    },
  ];

  return (
    <div className="dash-page">

      {/* ── Hero ── */}
      <section className="dash-hero">
        <div className="dash-hero-inner">
          <p className="dash-eyebrow">Your college application suite</p>
          <h1 className="dash-title">
            Welcome back{firstName ? `, ${firstName}` : ''}
            <span className="dash-wave"> 👋</span>
          </h1>
          <p className="dash-subtitle">
            Research, shortlist, and prepare for US universities — powered by AI, completely free.
          </p>
          <div className="dash-quick-links">
            <Link to="/how" className="dash-link-btn">
              <i className="fa-solid fa-book-open" /> How to Use
            </Link>
            <a
              href="https://forms.fillout.com/t/dnRhGLc7t8us"
              target="_blank"
              rel="noopener noreferrer"
              className="dash-link-btn"
            >
              <i className="fa-regular fa-star" /> Add a Review
            </a>
          </div>
        </div>
      </section>

      {/* ── Feature cards ── */}
      <section className="dash-cards-section">
        <div className="dash-cards-grid">
          {FEATURES.map(({ icon, title, desc, cta, href, accent, status }) => (
            <div key={title} className={`dash-card ${accent}`}>
              <div className="dash-card-top-row">
                <div className="dash-card-icon">{icon}</div>
                <span className={`dash-card-status dash-card-status--${status.type}`}>
                  {status.type === 'done'  && <i className="fa-solid fa-circle-check" />}
                  {status.type === 'count' && <i className="fa-solid fa-bookmark" />}
                  {status.type === 'todo'  && <i className="fa-regular fa-circle" />}
                  {status.label}
                </span>
              </div>
              <h2 className="dash-card-title">{title}</h2>
              <p className="dash-card-desc">{desc}</p>
              <Link to={href} className="dash-card-btn">{cta} →</Link>
            </div>
          ))}
        </div>
      </section>

    </div>
  );
};

export default Llama;
